import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createCanteen } from "../../../redux/actions/canteenActions";
import { useDispatch } from "react-redux";
import { useLanguage } from "../../../context/LanguageContext";

const SectionKChallenges = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    hasChallenges: "",
    k1: "",
    hasSuggestions: "",
    k2: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.hasChallenges) newErrors.hasChallenges = t.modules.kiosk.sections.K.errors?.selectYesNo || "Please select Yes or No";
    if (!form.hasSuggestions) newErrors.hasSuggestions = t.modules.kiosk.sections.K.errors?.selectYesNo || "Please select Yes or No";
    if (form.hasChallenges === t.global.yes && !form.k1.trim()) newErrors.k1 = t.modules.kiosk.sections.K.errors?.challengesRequired || "Field is required";
    if (form.hasSuggestions === t.global.yes && !form.k2.trim()) newErrors.k2 = t.modules.kiosk.sections.K.errors?.suggestionsRequired || "Field is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================================
     SAVE TO LOCAL STORAGE
  ================================ */

  const handleSave = () => {
    if (!validateForm()) return;
    try {
      
      const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
      surveyData.sectionK = form;
      localStorage.setItem("surveyData", JSON.stringify(surveyData));

      // Mark Section K as completed
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.k = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Determine if all sections are completed
      const allSections = [
        "a",
        "b",
        "c",
        "d",
        "e",
        "f",
        "g",
        "h",
        "i",
        "j",
        "k",
      ];
      const isTotalSurveyComplete = allSections.every(
        (sec) => completedSections[sec] === true,
      )
        ? "YES"
        : "NO";

      // Prepare FormData for submission
      const payload = new FormData();
      payload.append("municipality_id", "1");
      payload.append("ward_id", "5");
      payload.append("district_id", "10");
      payload.append("lattitude_longitude", "22.5726,88.3639");
      payload.append("survey_canteen_type", "kiosk");
      payload.append("program_name", "demo kiosk");
      payload.append("current_section", "final");
      payload.append("kiosk_location_fulladdress", "yeWWW");
      payload.append("reporting_date", "na");
      payload.append("area_id", "2");
      payload.append("canteen_type_id", "2");
      payload.append("challenges", form.hasChallenges === t.global.yes ? form.k1 : "No challenges");
      payload.append("suggestions", form.hasSuggestions === t.global.yes ? form.k2 : "No suggestions");
      payload.append("check_survey_completed", isTotalSurveyComplete);
      payload.append("is_total_survey_complete", isTotalSurveyComplete);

      // Dispatch Redux action
      dispatch(createCanteen(payload));

      navigate("/survey/sections");
    } catch (err) {
      console.error("Error saving data:", err);
      alert(t.global.alert_server_error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.K.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.K.subtitle}
              </p>
            </div>
            <div className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-full">
              {state?.canteenId
                ? `${t.global.canteen_id_label} ${state.canteenId}`
                : state?.kioskId
                  ? `${t.global.canteen_id_label} ${state.kioskId}`
                  : t.global.na}
            </div>
          </div>

          <div className="space-y-6">
            {/* K1 - Challenges */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                1. {t.modules.kiosk.sections.K.questions.challenges} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.hasChallenges === option
                        ? "bg-blue-50 border-blue-300"
                        : ""
                    } ${errors.hasChallenges ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="hasChallenges"
                      value={option}
                      checked={form.hasChallenges === option}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {form.hasChallenges === t.global.yes && (
                <textarea
                  name="k1"
                  placeholder={t.modules.kiosk.sections.K.placeholders?.challenges || "Describe challenges..."}
                  className={`w-full border p-4 rounded-lg ${errors.k1 ? "border-red-500" : ""}`}
                  rows="4"
                  value={form.k1}
                  onChange={handleChange}
                />
              )}
              {errors.k1 && (
                <p className="text-red-500 text-sm mt-1">{errors.k1}</p>
              )}
            </div>

            {/* K2 - Suggestions */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                2. {t.modules.kiosk.sections.K.questions.suggestions} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.hasSuggestions === option
                        ? "bg-blue-50 border-blue-300"
                        : ""
                    } ${errors.hasSuggestions ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="hasSuggestions"
                      value={option}
                      checked={form.hasSuggestions === option}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {form.hasSuggestions === t.global.yes && (
                <textarea
                  name="k2"
                  placeholder={t.modules.kiosk.sections.K.placeholders?.suggestions || "Provide suggestions..."}
                  className={`w-full border p-4 rounded-lg ${errors.k2 ? "border-red-500" : ""}`}
                  rows="4"
                  value={form.k2}
                  onChange={handleChange}
                />
              )}
              {errors.k2 && (
                <p className="text-red-500 text-sm mt-1">{errors.k2}</p>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                {t.global.submit_section_k || "Save Section K"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionKChallenges;